/**
 * Metamorphosis media hotspots.
 * A crossfading stack of product/background image pairs sourced from metaobjects
 * (identical behaviour to Product Media Slider), plus an optional overlay of
 * numbered hotspot buttons. Clicking a button reveals a tooltip card holding the
 * title + text of the matching product metaobject entry.
 * Supports:
 *  - smooth auto-play with loop (pauses on hover / pointer interaction)
 *  - prev / next arrow navigation
 *  - hotspot tooltips (one open at a time; close on outside click / Escape)
 */
class MetamorphosisMediaHotspots extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.mmh__track');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('.mmh__slide'));
    this.prevBtn = this.querySelector('.mmh__nav--prev');
    this.nextBtn = this.querySelector('.mmh__nav--next');
    this.index = 0;
    this.slideCount = this.slides.length;
    this.swiped = false;

    const { autoplay, autoplaySpeed } = this.dataset;
    this.autoplayEnabled = autoplay === 'true' && this.slideCount > 1;
    this.autoplayDelay = (Number(autoplaySpeed) || 4) * 1000;
    this.timer = null;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.slideCount > 1) {
      this.prevBtn?.addEventListener('click', () => this.handleNav(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.handleNav(this.index + 1));
      this.initSwipe();
      this.bindAutoplayPauseEvents();
      this.startAutoplay();
    }

    this.initHotspots();
    this.initTooltips();
    this.initZoom();
    this.update();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.modalHideTimer) clearTimeout(this.modalHideTimer);
    // A dialog torn down mid-transition would otherwise keep the scroll lock and
    // stay in the top layer until GC.
    if (this.modal?.open) this.modal.close();
  }

  /* ── Hotspots ──────────────────────────────────────────────
   * Each hotspot is a button + tooltip pair. Only one tooltip is open at
   * a time; opening one pauses autoplay so the crossfade doesn't slide the
   * artwork out from under the reader.
   */
  initHotspots() {
    this.hotspots = Array.from(this.querySelectorAll('.mmh__hotspot'));
    this.hotspotsLayer = this.querySelector('.mmh__hotspots');
    if (!this.hotspots.length) return;

    this.hotspots.forEach((hotspot) => {
      const dot = hotspot.querySelector('.mmh__dot');
      dot?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleHotspot(hotspot);
      });
    });

    // Close when clicking anywhere else, or pressing Escape.
    this.onDocClick = (event) => {
      if (!this.contains(event.target)) this.closeHotspots();
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.closeHotspots();
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  /* ── Tooltip "Read more" → modal ───────────────────────────
   * When the copy is longer than 3 lines, trim it to fit and append an
   * inline "Read more" link. Clicking it opens a popup with the full text.
   */
  initTooltips() {
    this.initModal();

    // Without a usable dialog there is nowhere for "Read more" to lead, so leave
    // the copy untruncated rather than offering a link that does nothing.
    if (!this.modal) return;

    const texts = Array.from(this.querySelectorAll('.mmh__tooltip-text'));
    texts.forEach((text) => {
      const copy = text.querySelector('.mmh__tooltip-copy');
      const moreBtn = text.querySelector('.mmh__tooltip-more');
      if (!copy || !moreBtn) return;

      const full = copy.textContent.trim();
      const tip = text.closest('.mmh__tooltip');
      const titleEl = tip?.querySelector('.mmh__tooltip-title');
      const title = titleEl ? titleEl.textContent.trim() : '';

      // Measure after layout so line wrapping is accurate.
      requestAnimationFrame(() => {
        const lineHeight = parseFloat(getComputedStyle(text).lineHeight) || 20;
        const maxHeight = lineHeight * 3 + 2; // allow 3 lines
        if (text.offsetHeight <= maxHeight) return; // fits — no link needed

        moreBtn.hidden = false;

        // Largest word count that fits alongside the "… Read more" link.
        const words = full.split(/\s+/);
        let lo = 0;
        let hi = words.length;
        let best = 0;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          copy.textContent = `${words.slice(0, mid).join(' ')}… `;
          if (text.offsetHeight <= maxHeight) {
            best = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }
        copy.textContent = `${words.slice(0, best).join(' ')}… `;

        moreBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.openModal(title, full);
        });
      });
    });
  }

  /* ── Modal ───────────────────────────────────────────────
   * A native <dialog>. showModal() gives us the top layer, focus trapping,
   * focus restore and Escape-to-close, so none of that is hand-rolled here.
   */
  initModal() {
    this.modal = this.querySelector('.mmh__modal');
    if (!this.modal || typeof this.modal.showModal !== 'function') {
      this.modal = null;
      return;
    }

    this.modalTitle = this.modal.querySelector('.mmh__modal-title');
    this.modalText = this.modal.querySelector('.mmh__modal-text');

    this.modal.querySelectorAll('[data-mmh-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeModal());
    });

    // Escape fires `cancel` and would close instantly, skipping the fade —
    // take it over so the exit transition still runs.
    this.modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeModal();
    });

    // A click on the dialog element itself is a click on the ::backdrop; the
    // inner .mmh__modal-dialog covers everything else.
    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeModal();
    });

    // Autoplay resumes however the dialog was dismissed.
    this.modal.addEventListener('close', () => this.startAutoplay());
  }

  openModal(title, text) {
    if (!this.modal || this.modal.open) return;

    // Close any open tooltip behind the popup.
    this.closeHotspots();

    if (this.modalTitle) {
      this.modalTitle.textContent = title;
      this.modalTitle.hidden = title === '';
    }
    if (this.modalText) this.modalText.textContent = text;

    if (this.modalHideTimer) clearTimeout(this.modalHideTimer);
    this.modal.showModal();
    // Next frame, so the transition has a start value to animate from — a dialog
    // is display:none until showModal(), and display changes don't transition.
    requestAnimationFrame(() => this.modal.classList.add('is-open'));
    this.stopAutoplay();
  }

  closeModal() {
    if (!this.modal || !this.modal.open) return;
    this.modal.classList.remove('is-open');
    // Let the fade finish before the dialog leaves the top layer.
    this.modalHideTimer = setTimeout(() => {
      if (this.modal.open) this.modal.close();
    }, 200);
  }

  toggleHotspot(hotspot) {
    const isOpen = hotspot.classList.contains('is-open');
    this.closeHotspots();
    if (!isOpen) this.openHotspot(hotspot);
  }

  openHotspot(hotspot) {
    hotspot.classList.add('is-open');
    hotspot.querySelector('.mmh__dot')?.setAttribute('aria-expanded', 'true');
    this.stopAutoplay();
  }

  closeHotspots() {
    if (!this.hotspots) return;
    this.hotspots.forEach((hotspot) => {
      hotspot.classList.remove('is-open');
      hotspot.querySelector('.mmh__dot')?.setAttribute('aria-expanded', 'false');
    });
    this.startAutoplay();
  }

  /* ── Image zoom ────────────────────────────────────────────
   * Opens a fullscreen PhotoSwipe lightbox when a product image is clicked
   * (same library + shared `.pswp` template the product pages use; the library
   * itself is fetched on the first zoom click by theme.loadPhotoswipe). Zoom is
   * bound to the <img> itself; the hotspot dots are separate elements in the
   * overlay above, so clicking a dot never reaches the image — the two
   * behaviours don't collide. Zoom is available on every slide's image.
   *
   * Metaobject MediaImages don't expose dimensions in Liquid, so the real
   * size is read from each loaded <img> (naturalWidth/Height).
   */
  initZoom() {
    if (this.dataset.zoom !== 'true') return;
    if (!document.querySelector('.pswp')) return;

    this.zoomImages = Array.from(this.querySelectorAll('.mmh-zoomable'));
    if (!this.zoomImages.length) return;

    this.zoomImages.forEach((img, i) => {
      img.addEventListener('click', () => this.openZoom(i));
    });
  }

  async openZoom(index) {
    // PhotoSwipe is a separate asset now — pull it in before the first zoom.
    if (typeof theme?.loadPhotoswipe !== 'function') return;
    try {
      await theme.loadPhotoswipe();
    } catch (err) {
      console.error(err);
      return;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const items = this.zoomImages.map((img) => {
      const nw = img.naturalWidth || 1200;
      const nh = img.naturalHeight || 1200;
      let w = nw;
      let h = nh;

      // PhotoSwipe only enables zoom when the image is larger than the
      // viewport. Our product images top out around 1200px, so on large
      // screens they'd refuse to zoom. When that happens, scale the reported
      // dimensions up so the image stays zoomable (bitmap just upscales).
      const fit = Math.min(vw / nw, vh / nh);
      if (fit >= 1) {
        const bump = fit * 1.08;
        w = Math.round(nw * bump);
        h = Math.round(nh * bump);
      }

      return {
        src: img.currentSrc || img.src,
        msrc: img.currentSrc || img.src,
        w,
        h,
        el: img,
      };
    });

    const isMobile = vw <= 768;
    const options = {
      allowPanToNext: false,
      captionEl: false,
      closeOnScroll: false,
      counterEl: false,
      history: false,
      index,
      pinchToClose: false,
      preloaderEl: false,
      scaleMode: 'fit',
      shareEl: false,
      tapToToggleControls: false,
      clickToCloseNonZoomable: false,
      maxSpreadZoom: isMobile ? 4 : 6,
      minSpreadZoom: 0.1,
      getDoubleTapZoom: (isMouseClick, item) => item.initialZoomLevel * 2.5,
      getThumbBoundsFn: (i) => {
        // PhotoSwipe wants document-relative coords (it subtracts the current
        // scroll itself). getBoundingClientRect is viewport-relative, so add
        // the scroll offset — otherwise the open/close animation starts
        // `scrollY` pixels off and the image visibly slides in from the wrong
        // spot when the page is scrolled.
        const rect = items[i].el.getBoundingClientRect();
        const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
        return { x: rect.left + scrollX, y: rect.top + scrollY, w: rect.width };
      },
    };

    // Zooming pauses autoplay so the slide doesn't crossfade underneath the
    // lightbox; resume when it closes.
    this.stopAutoplay();
    const gallery = new PhotoSwipe(document.querySelector('.pswp'), PhotoSwipeUI_Default, items, options);
    gallery.listen('destroy', () => this.startAutoplay());
    gallery.init();
  }

  /* ── Touch / mouse swipe ───────────────────────────────────
   * Slides crossfade in place rather than sitting in a scroll container, so
   * there is no native pan for the browser to hand us — the gesture has to be
   * recognised here.
   *
   * Touch uses touch events, NOT pointer events: mobile Safari cancels the
   * pointer stream (pointercancel, no pointerup) the moment it suspects the
   * finger might be scrolling the page, so a pointer-only handler never sees
   * the end of the swipe on a real device. Touch events always deliver
   * touchend, and calling preventDefault() on the first horizontal touchmove
   * claims the gesture outright. Mouse/pen keep using pointer events, which
   * are not cancelled that way.
   *
   * The slide only commits on release (no finger-following transform) because
   * a crossfade has no intermediate position to drag toward.
   */
  initSwipe() {
    const viewport = this.querySelector('.mmh__viewport');
    if (!viewport) return;

    const SWIPE_THRESHOLD = 40; // px of horizontal travel before it counts
    const AXIS_LOCK_SLOP = 8; // px of movement before deciding the direction

    let startX = 0;
    let startY = 0;
    let axis = null;
    let tracking = false;

    // Images are draggable by default, and a native image drag kills the
    // gesture mid-swipe on desktop.
    for (const img of this.querySelectorAll('.mmh__image, .mmh__bg-image')) {
      img.draggable = false;
    }

    const begin = (x, y, target) => {
      // Gestures that start on the arrows, a hotspot dot, or inside an open
      // tooltip belong to those controls, not to the slider.
      if (target.closest('button, a, .mmh__tooltip')) return;
      tracking = true;
      axis = null;
      startX = x;
      startY = y;
      this.swiped = false;
    };

    // Returns true once the gesture is ours (locked horizontal), so the touch
    // handler knows to take it away from the browser.
    const move = (x, y) => {
      if (!tracking) return false;
      if (!axis) {
        const dx = Math.abs(x - startX);
        const dy = Math.abs(y - startY);
        if (dx < AXIS_LOCK_SLOP && dy < AXIS_LOCK_SLOP) return false;
        // Lock to one axis on the first real movement: a mostly-vertical drag
        // belongs to the page, so drop the gesture and let it scroll.
        axis = dx > dy ? 'x' : 'y';
        if (axis === 'y') tracking = false;
      }
      return axis === 'x';
    };

    const end = (x) => {
      if (!tracking) return;
      tracking = false;
      const dx = x - startX;
      if (axis !== 'x' || Math.abs(dx) < SWIPE_THRESHOLD) return;
      // Flag the swipe so the click it generates doesn't also open the zoom
      // lightbox. Cleared by the next gesture, so a later tap still zooms.
      this.swiped = true;
      this.closeHotspots();
      this.handleNav(this.index + (dx < 0 ? 1 : -1));
    };

    const cancel = () => {
      tracking = false;
      axis = null;
    };

    viewport.addEventListener(
      'touchstart',
      (event) => {
        // More than one finger means pinch-zoom — leave it to the browser.
        if (event.touches.length !== 1) return cancel();
        const [touch] = event.touches;
        begin(touch.clientX, touch.clientY, event.target);
      },
      { passive: true }
    );

    // Non-passive: preventDefault() below is what stops the browser from
    // reinterpreting a horizontal swipe as a page scroll and cancelling it.
    viewport.addEventListener(
      'touchmove',
      (event) => {
        if (!tracking || event.touches.length !== 1) return;
        const [touch] = event.touches;
        if (move(touch.clientX, touch.clientY) && event.cancelable) event.preventDefault();
      },
      { passive: false }
    );

    viewport.addEventListener('touchend', (event) => {
      const [touch] = event.changedTouches;
      if (touch) end(touch.clientX);
    });

    viewport.addEventListener('touchcancel', cancel);

    // Mouse / pen only — touch is handled above, and running both would
    // advance the slide twice.
    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      begin(event.clientX, event.clientY, event.target);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      move(event.clientX, event.clientY);
    });

    viewport.addEventListener('pointerup', (event) => {
      if (event.pointerType === 'touch') return;
      end(event.clientX);
    });

    viewport.addEventListener('pointercancel', (event) => {
      if (event.pointerType === 'touch') return;
      cancel();
    });

    // Capture phase so this runs before the zoom listener bound to the image.
    viewport.addEventListener(
      'click',
      (event) => {
        if (!this.swiped) return;
        this.swiped = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /* ── Auto-play ─────────────────────────────────────────── */
  startAutoplay() {
    if (!this.autoplayEnabled || this.prefersReducedMotion) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.goTo(this.index + 1), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /* Pause autoplay while the cursor rests on the slider.
   *
   * Mouse / pen only. On touch, `pointerenter` fires on tap but `pointerleave`
   * frequently never does, so a single tap would stop autoplay permanently.
   * There is no hover to honour on touch anyway — the deliberate pauses (swipe,
   * open tooltip, zoom lightbox) are handled at their own call sites.
   */
  bindAutoplayPauseEvents() {
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      this.stopAutoplay();
    });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      // Don't resume while a tooltip is being read.
      if (!this.querySelector('.mmh__hotspot.is-open')) this.startAutoplay();
    });
  }

  handleNav(index) {
    this.stopAutoplay();
    this.goTo(index);
    this.startAutoplay();
  }

  goTo(index) {
    this.index = (index + this.slideCount) % this.slideCount;
    this.update();
  }

  update() {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.index);
    });

    // Hotspots annotate the first image only — hide the overlay on any other
    // slide and close any tooltip left open when leaving the first slide.
    if (this.hotspotsLayer) {
      const onFirst = this.index === 0;
      this.hotspotsLayer.classList.toggle('is-inactive', !onFirst);
      if (!onFirst && this.querySelector('.mmh__hotspot.is-open')) this.closeHotspots();
    }
  }
}

if (!customElements.get('metamorphosis-media-hotspots')) {
  customElements.define('metamorphosis-media-hotspots', MetamorphosisMediaHotspots);
}
